import * as _ from 'lodash';
import * as React from 'react';
import { connect } from 'react-redux';
import { DataShape, FormProps, reduxForm } from 'redux-form';
import { diagnostics } from 'ui-common';
import * as queryString from 'query-string';

import * as formUtils from 'login-utils/formUtils';
import * as onboardingForm from 'login-utils/onboardingForm';
import { formatUpfrontRatesParams, getUpfrontRates, getUpfrontRatesParams, UpfrontRate } from './upfrontRatesUtils';
import InputMap from 'login-components/Common/Inputs/InputMap';
import UpfrontRates from './UpfrontRates';
import { allInputs } from 'login-components/OnboardingForm/OnboardingForm';

interface StateProps {
  // we populate this form's initial values with another form's values
  onboardingFormValues: DataShape;
}
type UpfrontRatesContainerFormProps = FormProps<DataShape, StateProps, ReduxState>;
type UpfrontRatesContainerProps = UpfrontRatesContainerFormProps & StateProps & ReactRouter.RouteComponentProps<{}, {}>;

interface UpfrontRatesContainerState {
  disclaimer: string | null;
  hasError: boolean;
  inputs: FormInput[];
  latestResultsTimestamp: number | null;
  loading: boolean;
  submittedValues: DataShape;
  upfrontRates: UpfrontRate[];
  isRefinance: boolean;
}

const allPrefillableInputs = [
  onboardingForm.FIELDS.cashDownPayment,
  onboardingForm.FIELDS.purchasePrice,
  onboardingForm.FIELDS.propertyCounty,
  onboardingForm.FIELDS.estimatedCreditScore,
  onboardingForm.FIELDS.loanAmount,
  onboardingForm.FIELDS.propertyValue,
];

/**
 * Validation wrapper function that combines standard validation performed by formUtils.validate
 * and custom validation logic through addtlValidate param.
 * The purpose of this wrapper function is to reuse formUtils.validate for field-level validation
 * and avoid heavy refactoring. Meant to be used for dynamically populated input fields
 */
export const validateDynamicInput = (input: FormInput, addtlValidate?: (value: any, allValues: any) => any) => {
  return (value: any, allValues: any) => {
    if (addtlValidate) {
      const error = addtlValidate(value, allValues);
      if (error) {
        return error;
      }
    }
    const dataShape: DataShape = {};
    dataShape[input.name] = value;
    const errors = formUtils.validate([input], dataShape);
    return errors[_.first(_.keys(errors))!];
  };
};

function getPurchaseOnlyInputs(): FormInput[] {
  const purchasePriceInput = {
    ...InputMap.MoneyInput,
    identifier: 'purchase price',
    hidePlaceholder: true,
    name: onboardingForm.FIELDS.purchasePrice,
    label: `Purchase price`,
  };
  const cashDownPaymentInput = {
    ...InputMap.MoneyInput,
    identifier: 'down payment',
    hidePlaceholder: true,
    name: onboardingForm.FIELDS.cashDownPayment,
    label: `Down payment`,
  };
  return [
    {
      ...purchasePriceInput,
      validate: validateDynamicInput(purchasePriceInput, formUtils.generateValidatePositiveInteger(
        'Purchase price must be greater than or equal to 0.',
      ) as any),
    },
    {
      ...cashDownPaymentInput,
      validate: validateDynamicInput(cashDownPaymentInput, formUtils.cashDownPaymentValidator as any),
    },
  ];
}

function getRefiOnlyInputs(): FormInput[] {
  const propertyValueInput = {
    ...InputMap.MoneyInput,
    identifier: 'property value',
    hidePlaceholder: true,
    name: onboardingForm.FIELDS.propertyValue,
    label: `Property value`,
  };
  const loanAmountInput = {
    ...InputMap.MoneyInput,
    identifier: 'loan amount',
    hidePlaceholder: true,
    name: onboardingForm.FIELDS.loanAmount,
    label: `Loan amount`,
  };
  return [
    {
      ...propertyValueInput,
      validate: validateDynamicInput(propertyValueInput, formUtils.generateValidatePositiveInteger(
        'Property value must be greater than or equal to 0.',
      ) as any),
    },
    {
      ...loanAmountInput,
      validate: validateDynamicInput(loanAmountInput, formUtils.loanAmountValidator as any),
    },
  ];
}

function getSharedInputs(): FormInput[] {
  return [
    {
      ...InputMap.CountySelector,
      identifier: 'county',
      hidePlaceholder: true,
      name: onboardingForm.FIELDS.propertyCounty,
      label: 'County/Municipality',
    },
    {
      ...InputMap.SelectInput,
      identifier: 'estimated credit score',
      hidePlaceholder: true,
      name: onboardingForm.FIELDS.estimatedCreditScore,
      label: 'Estimated credit score',
      choices: onboardingForm.CREDIT_SCORE_CHOICES,
    },
  ];
}

/**
 * UpfrontRatesContainer is responsible for hooking up to Redux/Redux-Form and managing data fetching.
 */
export class UpfrontRatesContainer extends React.Component<UpfrontRatesContainerProps, UpfrontRatesContainerState> {
  state: UpfrontRatesContainerState = {
    disclaimer: null,
    hasError: false,
    inputs: [],
    latestResultsTimestamp: null,
    loading: false,
    submittedValues: {},
    upfrontRates: [],
    isRefinance: false,
  };

  prefillableInputs: string[] = [];

  async componentDidMount() {
    const { touch, location, onboardingFormValues } = this.props;
    const queryParams = queryString.parse(location.search);
    const redirectParams = getUpfrontRatesParams(queryParams);
    const initValues = !_.isEmpty(redirectParams) ? formatUpfrontRatesParams(redirectParams) : onboardingFormValues;

    // initialize the form when component first loads
    this.props.initialize!(initValues);
    // set up input fields based on loan purpose
    this.setupInputs(initValues.loanPurpose === 'REFINANCE');
    // fetch rates if everything is prefilled
    const prefilledValues = _.pick(initValues, this.prefillableInputs);
    if (_.keys(prefilledValues).length === this.prefillableInputs.length) {
      // pass down all initial values so everything gets retained on the form (this.props.initialize)
      await this.updateUpfrontRates(initValues);
    }
    // touch prefillable fields to kickoff validation.
    touch!(...this.prefillableInputs);
  }

  shouldShowInput(isRefinance: boolean, inputName: string): boolean {
    const inputsToExclude = isRefinance
      ? [onboardingForm.FIELDS.purchasePrice, onboardingForm.FIELDS.cashDownPayment]
      : [onboardingForm.FIELDS.propertyValue, onboardingForm.FIELDS.loanAmount];
    return !_.includes(inputsToExclude, inputName);
  }

  setupInputs = (isRefinance: boolean): void => {
    const inputsToShow = _.concat(isRefinance ? getRefiOnlyInputs() : getPurchaseOnlyInputs(), getSharedInputs());
    this.setState({
      isRefinance,
      inputs: inputsToShow,
    });
    this.prefillableInputs = _.filter(allPrefillableInputs, input => this.shouldShowInput(isRefinance, input));
  };

  updateUpfrontRates = async (values: DataShape) => {
    this.setState({ hasError: false, loading: true, submittedValues: _.pick(values, this.prefillableInputs) });

    try {
      const { disclaimer, upfrontRates } = await getUpfrontRates(values);
      // Reinitializing to the successfully submitted values will set the form to pristine and let us prevent the user
      // from resubmitting the same values. Don't reinitialize if request fails so user can retry.
      this.props.initialize!(values);
      this.setState({ disclaimer, latestResultsTimestamp: Date.now(), loading: false, upfrontRates });
    } catch (error) {
      diagnostics.error(error);
      this.setState({ hasError: true, loading: false });
    }
  };

  render() {
    const { children, handleSubmit, invalid, pristine, ...upfrontRatesProps } = this.props;
    const {
      disclaimer,
      hasError,
      inputs,
      latestResultsTimestamp,
      loading,
      submittedValues,
      upfrontRates,
      isRefinance,
    } = this.state;
    return (
      <UpfrontRates
        {...upfrontRatesProps}
        disabled={loading || invalid! || pristine!}
        disclaimer={disclaimer}
        hasError={hasError}
        inputs={inputs}
        latestResultsTimestamp={latestResultsTimestamp}
        loading={loading}
        onSubmit={handleSubmit!(this.updateUpfrontRates)}
        submittedValues={submittedValues}
        upfrontRates={upfrontRates}
        isRefinance={isRefinance}
      />
    );
  }
}

const ConnectedUpfrontRatesContainer = connect((state: ReduxState) => {
  /**
   * all fields from onboardingForm are pulled in here because we are re-initializing the form every time user clicks
   * Update Rates. the reason we are re-initializing the form is to use the "pristine" property of redux-form for
   * enabling/disabling the button. a better solution would be to use a different mechanism for disabling button so
   * that we don't have to re-initialize the form
   */
  const addtlFields = [
    onboardingForm.FIELDS.loanAmount,
    onboardingForm.FIELDS.propertyValue,
    onboardingForm.FIELDS.refiPurpose,
    onboardingForm.FIELDS.loanPurpose,
    onboardingForm.FIELDS.veteranStatus,
    onboardingForm.FIELDS.loanType,
    onboardingForm.FIELDS.propertySearchType,
    onboardingForm.FIELDS._timeline,
  ]; // TODO (UI-4113): remove when short-form supports refi
  const onboardingFormValues = onboardingForm.selector(
    state,
    ..._.concat(_.map(allInputs, input => input.name), addtlFields),
  );
  return {
    onboardingFormValues,
  };
})(UpfrontRatesContainer);

const FormConnectedUpfrontRatesContainer = reduxForm<any, any>({
  ...onboardingForm.FORM_CONFIG,
  validate: (values: DataShape) => formUtils.validate(getSharedInputs(), values) as any,
})(ConnectedUpfrontRatesContainer);

export default FormConnectedUpfrontRatesContainer;
